'use client';

import { useState } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { ThemeToggle } from '@/components/theme-toggle';

interface NavItem {
  label: string;
  href: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: 'Getting Started',
    items: [
      { label: 'Introduction', href: '#introduction' },
      { label: 'Quick Start', href: '#quick-start' },
      { label: 'Choose an Integration', href: '#integration-choice' },
    ],
  },
  {
    title: 'Authentication',
    items: [
      { label: 'API Keys', href: '#api-keys' },
      { label: 'Keep Keys Safe', href: '#security' },
      { label: 'Test Mode', href: '#test-cards' },
    ],
  },
  {
    title: 'SDK',
    items: [
      { label: 'Installation', href: '#sdk-installation' },
      { label: 'Using from a Website', href: '#sdk-browser' },
      { label: 'Methods', href: '#sdk-nodejs' },
    ],
  },
  {
    title: 'Payments API',
    items: [
      { label: 'Create Payment', href: '#create-payment' },
      { label: 'Parameters', href: '#payment-parameters' },
      { label: 'Skip the Form (autoRedirect)', href: '#auto-redirect' },
      { label: 'Account Configuration', href: '#supported-config' },
      { label: 'Payment Status', href: '#payment-status' },
      { label: 'Refunds', href: '#refunds' },
    ],
  },
  {
    title: 'S2S Payments',
    items: [
      { label: 'Overview', href: '#s2s-overview' },
      { label: 'Charge a Card', href: '#s2s-charge' },
      { label: 'Redirects and 3DS', href: '#s2s-3ds' },
      { label: 'Safe Retries', href: '#s2s-idempotency' },
      { label: 'Errors', href: '#s2s-errors' },
      { label: 'Complete Example', href: '#s2s-example' },
    ],
  },
  {
    title: 'Webhooks',
    items: [
      { label: 'Setup', href: '#webhook-config' },
      { label: 'Events', href: '#webhook-events' },
      { label: 'Request Format', href: '#webhook-payload' },
      { label: 'Retries', href: '#webhook-retry' },
    ],
  },
  {
    title: 'Integration',
    items: [
      { label: 'Redirect URLs', href: '#redirect-urls' },
      { label: 'Embed in an iframe', href: '#embed-iframe' },
      { label: 'Payment Methods', href: '#payment-methods' },
    ],
  },
  {
    title: 'Affiliate Tracking',
    items: [
      { label: 'Overview', href: '#affiliates' },
      { label: 'Tag a Payment', href: '#affiliate-tag' },
      { label: 'Get Totals', href: '#affiliate-breakdown' },
      { label: 'Manage Affiliates', href: '#affiliate-manage' },
    ],
  },
  {
    title: 'Plugins',
    items: [
      { label: 'WooCommerce', href: '#woocommerce' },
      { label: 'Shopify (Coming Soon)', href: '#shopify' },
    ],
  },
];

interface DocsSidebarProps {
  className?: string;
}

export function DocsSidebar({ className }: DocsSidebarProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(navSections.map((s) => s.title))
  );
  const [searchQuery, setSearchQuery] = useState('');

  const toggleSection = (title: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  const filteredSections = searchQuery
    ? navSections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) =>
            item.label.toLowerCase().includes(searchQuery.toLowerCase())
          ),
        }))
        .filter((section) => section.items.length > 0)
    : navSections;

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', href);
    }
  };

  return (
    <nav className={className}>
      {/* Search */}
      <div className="px-4 pt-4 pb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search docs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 bg-card/70 border border-border rounded-xl text-sm text-foreground shadow-sm placeholder-muted-foreground focus:outline-none focus:border-primary/60 focus:ring-4 focus:ring-primary/10"
          />
        </div>
      </div>

      {/* Nav Sections */}
      <div className="px-2 pb-6 space-y-1">
        {filteredSections.map((section) => {
          const isExpanded = expandedSections.has(section.title);
          return (
            <div key={section.title}>
              <button
                onClick={() => toggleSection(section.title)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-foreground rounded-xl hover:bg-accent/70 transition-colors"
              >
                <span>{section.title}</span>
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                )}
              </button>

              {isExpanded && (
                <div className="ml-2 mt-0.5 space-y-0.5">
                  {section.items.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      onClick={(e) => handleNavClick(e, item.href)}
                      className="block px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/60 rounded-lg transition-colors border-l-2 border-transparent hover:border-primary"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* API Reference link */}
        <div className="pt-2 mt-2 border-t border-border">
          <a
            href="/api-docs"
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-foreground hover:text-foreground rounded-md hover:bg-accent/50 transition-colors"
          >
            API Reference
            <svg className="w-3.5 h-3.5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>

        {/* Support link */}
        <a
          href="mailto:support@fluxpay.online"
          className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-foreground hover:text-foreground rounded-md hover:bg-accent/50 transition-colors"
        >
          Support
        </a>

        {/* Theme Toggle */}
        <div className="pt-2 mt-2 border-t border-border px-3 py-2">
          <ThemeToggle />
        </div>
      </div>
    </nav>
  );
}
